import { useEffect, useState } from 'react';
import { type FilmRequest, api, ApiError, getToken, setToken, type Brand, type LoginResult, type Workspace } from './lib/api';
import { Assets } from './components/Assets';
import { ThemeToggle } from './components/ThemeToggle';
import { CardDefaults } from './components/CardDefaults';
import { FlightDeck } from './components/FlightDeck';
import { Hooks } from './components/Hooks';
import { SignIn } from './components/SignIn';
import { Connectors } from './components/Connectors';
import { Schedule } from './components/Schedule';
import { Channels } from './components/Channels';
import { Record } from './components/Record';
import { Stage } from './components/Stage';
import { Live } from './components/Live';
import { GreenRoom } from './components/GreenRoom';
import { Sources } from './components/Sources';
import { BrandKit } from './components/BrandKit';
import { Settings } from './components/Settings';
import { Credits } from './components/Credits';
import { Catalogue } from './components/Catalogue';
import { CreditPill } from './components/CreditPill';
import { Toast, type ToastState } from './components/Toast';
import { creditEvents, type Short } from './lib/creditEvents';
import { Lab } from './components/Lab';
import { AudioLab } from './components/AudioLab';
import { StartHere } from './components/StartHere';
import type { Card, Idea } from './lib/api';
import { VideoLab } from './components/VideoLab';
import { Spyglass } from './components/Spyglass';
import { Talent } from './components/Talent';
import { DirectorRail } from './components/DirectorRail';
import { focusMove } from './lib/moves';
import { readNav, writeNav, navDefault } from './lib/nav';
import type { Defaults, Proposal } from './lib/api';
import { Renders } from './components/Renders';
import { ModeSwitch } from './components/ModeSwitch';
import { Quick } from './components/quick/Quick';
import { readMode, writeMode, type Mode } from './lib/mode';
import { PRO_HANDOFF, QUICK_TABS, TAB_LABEL, type Handoff, type QuickTab } from './lib/quick';


/** The door: where a person lands after signing out. */
const DOOR_URL = 'https://rookeryos.com/';
// The sidebar reads top to bottom as the order the work happens in: set the
// show up once, gather what it will use, make the episode, ship it. The
// groups are the journey; the ids are NOT touched, deliberately. The
// assistant director addresses a screen by id (its Bench union in
// directorBoard.ts) and the rail passes that straight into setView behind an
// `as ViewId` cast — so a rename would compile and then quietly land the
// creator on Assets. Group order is derived from first appearance here
// (`[...new Set(...)]` below), so this array is the whole mechanism.
const VIEWS = [
  // 199: the first step the studio never had.
  { id: 'start', group: 'Start here', label: 'Start here' },
  // The Quick Studio: the second door onto the same rooms. Six tabs, held as
  // state on this one view rather than as six ids, so the director's Bench
  // union and the Pro nav are untouched. Hidden from the Pro nav below.
  { id: 'quick', group: 'Start here', label: 'Quick studio' },

  // Once, per show.
  { id: 'kit', group: 'Set up', label: 'Brand kit' },
  // T228: everyone who reads on the shows, across the kits; an AI avatar is talent too.
  { id: 'talent', group: 'Set up', label: 'Talent' },
  // The Lab makes a show's graphics: the plate, the lower third, the bug.
  { id: 'lab', group: 'Set up', label: 'The Lab' },

  // What an episode will be cut from.
  { id: 'sources', group: 'Gather', label: 'Sources' },
  { id: 'assets', group: 'Gather', label: 'Assets' },
  // Spyglass makes recordings from screen takes.
  { id: 'spyglass', group: 'Gather', label: 'Spyglass' },
  { id: 'record', group: 'Gather', label: 'Record' },

  // The episode itself, from the idea to the cut.
  { id: 'hooks', group: 'Make', label: 'Hooks & SEO' },
  { id: 'deck', group: 'Make', label: 'Flight Deck' },
  // 196: lines in a cast's voice and a bed from a brief, into the library.
  { id: 'audio', group: 'Make', label: 'Audio Lab' },
  { id: 'video', group: 'Make', label: 'Video Lab' },

  // Out of the door.
  { id: 'renders', group: 'Ship', label: 'Renders' },
  { id: 'schedule', group: 'Ship', label: 'Schedule' },
  { id: 'channels', group: 'Ship', label: 'Channels' },
  { id: 'connectors', group: 'Ship', label: 'Connectors' },
  { id: 'live', group: 'Ship', label: 'Live' },

  // Its own group at the bottom. It is not part of making a video, and putting
  // it inside one of the groups that are would bury the only screen that
  // answers "how much have I used" and "who else is in here".
  // 204: the credits — what the workspace holds and where they went. Reachable in both modes.
  { id: 'models', group: 'Workspace', label: 'Models' },
  { id: 'credits', group: 'Workspace', label: 'Credits' },
  { id: 'settings', group: 'Workspace', label: 'Settings' },
] as const;
type ViewId = (typeof VIEWS)[number]['id'];

export default function App() {
  // A guest's door is public: /green/<code> renders without an account, and
  // never the studio. Decided before any state so nothing of the studio loads.
  const greenSlug = /^\/green\/([a-z0-9]{12,24})\/?$/.exec(window.location.pathname)?.[1];
  if (greenSlug) return <GreenRoom slug={greenSlug} />;
  // The stage (G2) is what the media server's egress records: public, keyed
  // by the recorder token on its query, never the studio.
  const stageSlug = /^\/stage\/([a-z0-9]{12,24})\/?$/.exec(window.location.pathname)?.[1];
  if (stageSlug) return <Stage slug={stageSlug} />;
  const [authed, setAuthed] = useState(Boolean(getToken()));
  const [account, setAccount] = useState<LoginResult['user'] | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandId, setBrandId] = useState<string | null>(null);
  // A workspace that has a show has been here before and wants the board; one
  // that has none has never got past the first wall, so it gets the door.
  // Settled once, when the brands first load, and never fought with again.
  const [view, setView] = useState<ViewId>(() => (readMode() === 'quick' ? 'quick' : 'deck'));
  const [landed, setLanded] = useState(false);
  // Quick or Pro: remembered per browser; unset until the landing rule below
  // settles it once from whether the workspace has a show.
  const [mode, setModeState] = useState<Mode>(() => readMode() ?? 'pro');
  const [quickTab, setQuickTab] = useState<QuickTab>('image');
  // The menu can shrink to nothing and come back on a click (lib/nav.ts).
  const [navOpen, setNavOpen] = useState<boolean>(() => readNav() ?? navDefault(typeof window === 'undefined' ? 1440 : window.innerWidth));
  function toggleNav() { setNavOpen((was) => { writeNav(!was); return !was; }); }
  // A card the Hooks page produced, to open the moment the deck shows.
  const [openCard, setOpenCard] = useState<string | null>(null);
  // P3: a scene sent to the Record screen to be filmed; cleared when the take lands.
  const [film, setFilm] = useState<FilmRequest | null>(null);
  const [recordScreen, setRecordScreen] = useState(false);
  // 155: the workspace's defaults — read once a session, handed to every lab.
  const [defaults, setDefaults] = useState<Defaults>({ version: 1 });
  // T232: the director's proposals, lifted so the nav can badge each bench.
  const [proposals, setProposals] = useState<Proposal[]>([]);
  // D16: the next episode starts from what worked — a seed for Hooks & SEO.
  const [hookSeed, setHookSeed] = useState<{ topic?: string | null; pain?: string | null } | null>(null);
  // 200: what the Idea Lab handed to a bench when a proposal was accepted.
  // The bench reads it once and clears it — a prefill that lingers would
  // refill the form the next time the creator walked past.
  const [prefill, setPrefill] = useState<Idea | null>(null);
  // 205: the Credits page names a model; the catalogue opens on it once, then forgets.
  const [catalogueOpen, setCatalogueOpen] = useState<{ bench?: string; modelId?: string } | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [error, setError] = useState<string | null>(null);
  // 204: a refusal on credits is said where the creator stands, with the one button that answers it.
  const [toast, setToast] = useState<ToastState | null>(null);
  useEffect(() => {
    const onShort = (e: Event) => {
      const d = (e as CustomEvent<Short>).detail;
      setToast({ text: d.shortBy ? `Short by ${d.shortBy.toLocaleString()} credits — ${Math.max(0, Math.floor(d.balance?.plan ?? 0) + Math.floor(d.balance?.purchased ?? 0)).toLocaleString()} left.` : d.message, action: { label: 'Buy credits', onClick: () => setView('credits') } });
    };
    creditEvents.addEventListener('short', onShort);
    return () => creditEvents.removeEventListener('short', onShort);
  }, []);

  // A token in localStorage survives a reload but says nothing about whether the
  // seat behind it still exists. /auth/me is the authority; a 401 here signs out.
  useEffect(() => {
    if (!authed || account) return;
    api.me()
      .then((r) => {
        setAccount(r.user);
        setWorkspace(r.tenant);
        api.defaults().then((d) => setDefaults(d.defaults)).catch(() => undefined);
        // The switcher used to be fed only by the sign-in response, so a page
        // reload emptied this list and the control vanished — taking the only
        // route to /auth/switch with it.
        setWorkspaces(r.workspaces);
      })
      .catch((e) => {
        // Only a 401 means the session is over. This caught EVERYTHING — a
        // network blip, a 500, a cold start that timed out — and answered all
        // of them by signing the person out, which reads as "your session
        // expired" and is a lie about what happened.
        if ((e as ApiError).status === 401) { setToken(null); setAuthed(false); return; }
        setError(`Could not load your account — ${(e as Error).message}`);
      });
  }, [authed, account]);

  useEffect(() => {
    if (!authed) return;
    api.cards().then((r) => setCards(r.cards)).catch(() => undefined);
    api.brands()
      .then((b) => {
        setBrands(b); setBrandId((id) => id ?? b[0]?.id ?? null);
        setLanded((was) => { if (!was && b.length === 0) setView('start'); return true; });
        // The landing rule for the door: nobody has chosen yet, so a
        // workspace with no show gets Quick and one with a show gets Pro.
        // Written once; the switch owns it from here.
        if (readMode() === null) {
          const m: Mode = b.length === 0 ? 'quick' : 'pro';
          writeMode(m); setModeState(m);
          if (m === 'quick') setView('quick');
        }
      })
      .catch((e) => {
        setError((e as Error).message);
        if ((e as { status?: number }).status === 401) setAuthed(false);
      });
  }, [authed]);

  if (!authed) {
    return (
      <SignIn
        onSignedIn={(r) => {
          setAccount(r.user);
          setWorkspace(r.tenant);
          setWorkspaces(r.workspaces);
          setAuthed(true);
        }}
      />
    );
  }

  const brand = brands.find((b) => b.id === brandId) ?? null;
  const groups = [...new Set(VIEWS.map((v) => v.group))];

  /** The switch: remembered, and it lands somewhere sensible in both directions. */
  function setMode(m: Mode) {
    if (m === mode) return;
    writeMode(m); setModeState(m);
    if (m === 'quick') { if (view !== 'start' && view !== 'settings' && view !== 'credits' && view !== 'models') setView('quick'); }
    else if (view === 'quick') setView(PRO_HANDOFF[quickTab].bench as ViewId);
  }
  /** "Open in Pro": the bench that owns the work, the control lit, the form carried across. */
  function openInPro(h: Handoff) {
    setPrefill(h.prefill ?? null);
    if (h.cardId) setOpenCard(h.cardId);
    writeMode('pro'); setModeState('pro');
    setView(h.bench as ViewId);
    if (h.move) focusMove(h.move);
  }
  const navButton = (v: (typeof VIEWS)[number]) => (
    <button key={v.id} className={v.id === view ? 'on' : ''} onClick={() => setView(v.id)}>
      {v.label}
      {proposals.some((p) => p.bench === v.id) && <span className="nav-n" title="The director proposes something here">{proposals.filter((p) => p.bench === v.id).length}</span>}
    </button>
  );

  return (
    <div className={`shell${navOpen ? '' : ' nav-closed'}`}>
      <div className="topbar">
        <button type="button" className="navtoggle" data-tool="nav-toggle" onClick={toggleNav}
                aria-expanded={navOpen} aria-controls="studio-nav"
                aria-label={navOpen ? 'Hide the menu' : 'Show the menu'} title={navOpen ? 'Hide the menu' : 'Show the menu'}>
          <span aria-hidden="true">{navOpen ? '\u2039' : '\u2261'}</span>
        </button>
        <span className="mark"><span className="bars" />Rookery</span>
        {brands.length > 0 && (
          <select value={brandId ?? ''} onChange={(e) => setBrandId(e.target.value || null)} aria-label="Brand">
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        {workspaces.length > 1 && (
          <select
            value={workspace?.id ?? ''}
            aria-label="Workspace"
            onChange={(e) => {
              // Switching mints a NEW token from a re-read membership; the old
              // token is not edited, so a revoked seat cannot be carried over.
              void api.switchWorkspace(e.target.value).then((r) => {
                setToken(r.token);
                setWorkspace(r.tenant);
                setBrandId(null);
                setAccount(null);
              });
            }}
          >
            {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        )}
        <span className="spacer" />
        <span className="who">
          {workspace?.name}
          <b>{account?.name ?? account?.email}</b>
        </span>
        <CreditPill onOpen={() => setView('credits')} />
        <ModeSwitch mode={mode} onChange={setMode} />
        <ThemeToggle />
        <button className="btn" onClick={() => {
          setToken(null); setAuthed(false); setAccount(null); setWorkspace(null); setWorkspaces([]);
          // Signing out of the studio lands on the door, not on the studio's sign-in: the content side is the front of the house.
          window.location.href = DOOR_URL;
        }}>Sign out</button>
      </div>

      <nav id="studio-nav" aria-hidden={!navOpen}>
        {mode === 'pro' ? groups.map((g) => (
          // Pro: the journey, as before. The Quick row is the switch's job, not a nav entry.
          <div key={g} style={{ marginBottom: 14 }}>
            <p className="lbl">{g}</p>
            {VIEWS.filter((v) => v.group === g && v.id !== 'quick').map(navButton)}
          </div>
        )) : (
          // Quick: Start here, the six tabs, Settings. Nothing else to learn.
          <>
            <div style={{ marginBottom: 14 }}>
              <p className="lbl">Start here</p>
              {VIEWS.filter((v) => v.id === 'start').map(navButton)}
            </div>
            <div style={{ marginBottom: 14 }} data-tool="quick-nav">
              <p className="lbl">Quick</p>
              {QUICK_TABS.map((t) => (
                <button key={t} className={view === 'quick' && quickTab === t ? 'on' : ''} onClick={() => { setQuickTab(t); setView('quick'); }}>{TAB_LABEL[t]}</button>
              ))}
            </div>
            <div style={{ marginBottom: 14 }}>
              <p className="lbl">Workspace</p>
              {VIEWS.filter((v) => v.id === 'models' || v.id === 'credits' || v.id === 'settings').map(navButton)}
            </div>
          </>
        )}
      </nav>

      <main>
        {error && <div className="err">{error}</div>}
        <Toast toast={toast} onClose={() => setToast(null)} />
        {/* T229: the director on every bench — this bench's proposals first, the rest a step away. */}
        {/* The Idea Lab is Quick's director; the rail is Pro's. */}
        {mode === 'pro' && <DirectorRail view={view} onItems={setProposals} onGo={(b, move) => { setView(b as ViewId); focusMove(move); }} />}
        {/* Hooks deliberately does not navigate away after creating a card.
            A person who just generated six scored variants may want a card from
            more than one of them, and jumping to the board would throw the
            other five away. It confirms in place and says where the card went. */}
        {view === 'quick' ? (
          <Quick brand={brand} tab={quickTab} onTab={setQuickTab} onOpenInPro={openInPro}
                 onGo={(b, move, p) => { setPrefill(p ?? null); setView(b as ViewId); if (move) focusMove(move); }}
                 prefill={prefill} onUsedPrefill={() => setPrefill(null)}
                 cards={cards} onCardMade={(c) => setCards((all) => [c, ...all])} />
        ) : view === 'start' ? (
          <StartHere brands={brands} brand={brand} cards={cards}
                     onGo={(b, move, p) => {
                       setPrefill(p ?? null);
                       // In Quick, a storyboard proposal lands on the Video tab rather than the Scene Builder.
                       if (mode === 'quick' && b === 'video' && p?.action === 'plan_scenes') { setQuickTab('video'); setView('quick'); return; }
                       setView(b as ViewId); if (move) focusMove(move);
                     }}
                     onBrandMade={(b) => { setBrands((all) => [...all, b]); setBrandId((id) => id ?? b.id); }}
                     onCardMade={(c) => setCards((all) => [c, ...all])} />
        ) : view === 'hooks' ? (
          <Hooks brand={brand} seed={hookSeed} onOpenCard={(cid) => { setOpenCard(cid); setView('deck'); }} />
        ) : view === 'deck' ? (
          <FlightDeck brand={brand} openId={openCard} onOpened={() => setOpenCard(null)}
                      onFilm={(f) => { setFilm(f); setView('record'); }}
                      onNextEpisode={(seed) => { setHookSeed(seed); setView('hooks'); }} />
        ) : view === 'connectors' ? (
          <Connectors brand={brand} />
        ) : view === 'schedule' ? (
          <Schedule />
        ) : view === 'channels' ? (
          <Channels brand={brand} />
        ) : view === 'live' ? (
          <Live brand={brand} onOpenCard={(cid) => { setOpenCard(cid); setView('deck'); }} />
        ) : view === 'spyglass' ? (
          <Spyglass brand={brand} defaults={defaults} onRecord={() => { setRecordScreen(true); setView('record'); }} onOpenSources={() => setView('sources')} onOpenCard={(cid) => { setOpenCard(cid); setView('deck'); }} />
        ) : view === 'lab' ? (
          <Lab brand={brand} defaults={defaults} />
        ) : view === 'video' ? (
          <VideoLab brand={brand} prefill={prefill} onUsedPrefill={() => setPrefill(null)} />
        ) : view === 'audio' ? (
          <AudioLab brand={brand} prefill={prefill} onUsedPrefill={() => setPrefill(null)} />
        ) : view === 'talent' ? (
          <Talent brands={brands} defaults={defaults} onDefaults={setDefaults} onBrandSaved={(b) => setBrands((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...b } : x)))} onOpenKit={(id) => { setBrandId(id); setView('kit'); }} />
        ) : view === 'kit' ? (
          <>
            <BrandKit
              workspace={workspace}
              brand={brand}
              onSaved={(b) => setBrands((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...b } : x)))}
            />
            {/* Only with a brand to hang them on: the defaults live on the
                brand, because that is where everything else that should not
                change between videos already lives. */}
            {brand && (
              <CardDefaults
                brand={brand}
                onSaved={(b) => setBrands((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...b } : x)))}
              />
            )}
          </>
        ) : view === 'sources' ? (
          <Sources brand={brand} onOpenCard={(cid) => { setOpenCard(cid); setView('deck'); }} />
        ) : view === 'record' ? (
          <Record key={recordScreen ? 'screen' : 'camera'} brand={brand} film={film} startOnScreen={recordScreen} defaults={defaults} onOpenCard={(cid) => { setFilm(null); setOpenCard(cid); setView('deck'); }} />
        ) : view === 'renders' ? (
          <Renders />
        ) : view === 'models' ? (
          <Catalogue open={catalogueOpen} onOpened={() => setCatalogueOpen(null)} />
        ) : view === 'credits' ? (
          <Credits role={workspace?.role ?? null} onOpenModel={(bench, modelId) => { setCatalogueOpen({ ...(bench ? { bench } : {}), modelId }); setView('models'); }} />
        ) : view === 'settings' ? (
          <Settings
            account={account}
            workspace={workspace}
            onBrandsChanged={() => { void api.brands().then(setBrands); }}
            onOpenCard={(cid) => { setOpenCard(cid); setView('deck'); }}
            onOpenModels={() => setView('models')}
            defaults={defaults}
            onDefaults={setDefaults}
            brands={brands}
          />
        ) : (
          // Every entry in VIEWS has a view. There is deliberately no
          // "not built yet" fallback: a nav item that opens an empty page is
          // the thing that made this look like a scaffold, so anything without
          // a working screen stays out of the nav until it has one.
          <Assets brand={brand} prefill={prefill} onUsedPrefill={() => setPrefill(null)} />
        )}
      </main>
    </div>
  );
}
